import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { hasPendingDiagnostic, savePendingDiagnostic } from '../utils/pendingDiagnosticStorage';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const { signup, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const stateFormData = location.state?.formData;
  const stateFrom = location.state?.from;
  const stateDiagnosticId = location.state?.diagnosticId;

  const passwordCriteria = [
    { label: 'Au moins 8 caractères',                      met: password.length >= 8 },
    { label: 'Au moins une majuscule',                     met: /[A-Z]/.test(password) },
    { label: 'Au moins une minuscule',                     met: /[a-z]/.test(password) },
    { label: 'Au moins un chiffre',                        met: /[0-9]/.test(password) },
    { label: 'Au moins un caractère spécial (!@#$%^&*...)', met: /[^A-Za-z0-9]/.test(password) },
  ];

  const allCriteriaMet = passwordCriteria.every(c => c.met);
  const showCriteria = passwordFocused || password.length > 0;

  const validatePassword = (password: string): { isValid: boolean; message: string } => {
    if (password.length < 8) {
      return { isValid: false, message: 'Le mot de passe doit contenir au moins 8 caractères' };
    }
    if (!/[A-Z]/.test(password)) {
      return { isValid: false, message: 'Le mot de passe doit contenir au moins une majuscule' };
    }
    if (!/[a-z]/.test(password)) {
      return { isValid: false, message: 'Le mot de passe doit contenir au moins une minuscule' };
    }
    if (!/[0-9]/.test(password)) {
      return { isValid: false, message: 'Le mot de passe doit contenir au moins un chiffre' };
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return { isValid: false, message: 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*...)' };
    }
    return { isValid: true, message: '' };
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!email || !password || !confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      toast.error(passwordValidation.message);
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      setLoading(true);
      let hasPending = hasPendingDiagnostic();

      if (!hasPending && stateFormData) {
        savePendingDiagnostic(stateFormData);
        hasPending = true;
      }

      // Les clients n'ont pas de prénom/nom — passés vides
      await signup(email, password, '', '');

      if (hasPending) {
        toast.loading('Connexion et sauvegarde de votre diagnostic...', { id: 'signup-flow' });

        try {
          const loginResult = await login(email, password);

          if (loginResult.savedDiagnosticId) {
            toast.success('Compte créé et diagnostic sauvegardé avec succès !', { id: 'signup-flow' });
            navigate('/checkout', {
              state: { diagnosticId: loginResult.savedDiagnosticId }
            });
            return;
          } else {
            toast.error('Compte créé mais erreur lors de la sauvegarde du diagnostic', { id: 'signup-flow' });
            navigate('/login');
            return;
          }
        } catch (loginError) {
          console.error('Error during auto-login:', loginError);
          toast.error('Compte créé. Veuillez vous connecter.', { id: 'signup-flow' });
          navigate('/login');
          return;
        }
      } else {
        toast.loading('Connexion en cours...', { id: 'signup-flow' });
        try {
          await login(email, password);
          toast.success('Compte créé avec succès !', { id: 'signup-flow' });
          navigate('/dashboard');
        } catch {
          toast.success('Compte créé ! Vous pouvez maintenant vous connecter.', { id: 'signup-flow' });
          navigate('/login');
        }
      }
    } catch (error: any) {
      console.error('Erreur lors de la création du compte:', error);
      if (error.message?.includes('already registered')) {
        toast.error('Cette adresse email est déjà utilisée');
      } else if (error.message?.includes('invalid email')) {
        toast.error('Adresse email invalide');
      } else if (error.message?.includes('Password should be')) {
        toast.error('Le mot de passe doit contenir au moins 6 caractères');
      } else {
        toast.error('Erreur lors de la création du compte');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-slate-900/20" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sm:mx-auto sm:w-full sm:max-w-md relative z-10"
      >
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Créer un compte
        </h2>
        <p className="mt-2 text-center text-sm text-slate-300">
          Ou{' '}
          <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">
            connectez-vous à votre compte existant
          </Link>
        </p>
      </motion.div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 py-8 px-4 shadow-2xl rounded-2xl sm:px-10 hover:border-white/20 transition-all duration-300">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300">
                Adresse email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                  className="appearance-none block w-full px-3 py-2 bg-slate-800/50 border border-white/10 rounded-lg shadow-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300">
                Mot de passe
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  className="appearance-none block w-full px-3 py-2 bg-slate-800/50 border border-white/10 rounded-lg shadow-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {showCriteria && (
                <ul className="mt-2 space-y-1.5 bg-slate-800/30 p-3 rounded-lg border border-white/5">
                  {passwordCriteria.map((criterion) => (
                    <li key={criterion.label} className="flex items-center gap-2">
                      {/* Icône avec crossfade ✗ → ✓ */}
                      <span className="relative flex-shrink-0 w-4 h-4">
                        <svg
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className={`absolute inset-0 w-4 h-4 text-green-400 transition-opacity duration-300 ${criterion.met ? 'opacity-100' : 'opacity-0'}`}
                        >
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <svg
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className={`absolute inset-0 w-4 h-4 text-red-400 transition-opacity duration-300 ${criterion.met ? 'opacity-0' : 'opacity-100'}`}
                        >
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </span>
                      <span className={`text-sm transition-colors duration-300 ${criterion.met ? 'text-green-400' : 'text-slate-400'}`}>
                        {criterion.label}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300">
                Confirmer le mot de passe
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 bg-slate-800/50 border border-white/10 rounded-lg shadow-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !allCriteriaMet}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? 'Création...' : 'Créer mon compte'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
